import { useCallback, useEffect, useRef, useState } from "react";
import type { MessageViewPhase } from "./render-model";

/**
 * streaming 中は Activity 履歴の開閉を利用者の操作に委ね、
 * 処理が完了したときだけ回答を優先して閉じる。
 */
export function useActivityPresentation({
  hasVisibleAnswer,
  initialStartedAtMs,
  phase,
}: {
  hasVisibleAnswer: boolean;
  initialStartedAtMs?: number;
  phase: MessageViewPhase;
}) {
  const isWorking = phase === "live" && !hasVisibleAnswer;
  const [open, setOpen] = useState(phase === "incomplete");
  const manuallyChanged = useRef(false);
  const [durationSeconds, setDurationSeconds] = useState<number>();
  const elapsedBeforeCurrentSegmentMs = useRef(0);
  const [workingTiming, setWorkingTiming] = useState(() => {
    if (!isWorking) return null;
    return {
      elapsedBeforeCurrentSegmentMs: 0,
      startedAtMs: initialStartedAtMs ?? Date.now(),
    };
  });
  const currentSegmentStartedAtMs = useRef(workingTiming?.startedAtMs ?? null);

  useEffect(() => {
    if (phase === "done") {
      manuallyChanged.current = false;
      setOpen(false);
    }
  }, [phase]);

  useEffect(() => {
    if (phase === "incomplete") {
      if (!manuallyChanged.current) setOpen(true);
    }

    if (isWorking) {
      if (currentSegmentStartedAtMs.current === null) {
        const startedAtMs = Date.now();
        currentSegmentStartedAtMs.current = startedAtMs;
        setWorkingTiming({
          elapsedBeforeCurrentSegmentMs: elapsedBeforeCurrentSegmentMs.current,
          startedAtMs,
        });
      }
      return;
    }

    if (currentSegmentStartedAtMs.current !== null) {
      elapsedBeforeCurrentSegmentMs.current +=
        Date.now() - currentSegmentStartedAtMs.current;
      currentSegmentStartedAtMs.current = null;
      setWorkingTiming(null);
      setDurationSeconds(
        Math.max(1, Math.floor(elapsedBeforeCurrentSegmentMs.current / 1000)),
      );
    }
  }, [isWorking, phase]);

  const onOpenChange = useCallback((nextOpen: boolean) => {
    manuallyChanged.current = true;
    setOpen(nextOpen);
  }, []);

  return { open, onOpenChange, durationSeconds, workingTiming };
}
